import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'; // 가정: 인증 및 API 요청을 처리하는 AuthContext
import { LeagueDailyLineupPlayerResponse, LeagueDetailDto, LeagueDto, LeagueLineupPlayerDto, LeagueSessionParticipantRankResponse } from '@playball/common/dto/League';
import AdComponent from '../ads/AdComponent';
import { KBOGamePlayerStatsHitterDataDto, KBOGamePlayerStatsPitcherDataDto, KBOLeaguePositionRuleDto, KBOLeagueRuleResponse } from '@playball/common/dto/KBO';
import { NewPodium } from '../UserRankingPage';
import { DataResponse } from '@playball/common/dto/Common';

interface LineupComponentProps {
    league: LeagueDetailDto;
}

const TEAM_NAME_MAP: { [key: string]: string } = {
  'LG': '트윈스', 
  'SSG': '랜더스',
  'KT': '위즈', 
  'NC': '다이노스',
  'KIA': '타이거즈',
  '롯데': '자이언츠',
  '두산': '베어스',
  '삼성': '라이온즈', 
  '한화': '이글스', 
  '키움': '히어로즈',
}

const POSITION_MAP: { [key: string]: string } = {
  'P': '투수', 
  'C': '포수',
  '1B': '1루수',
  '2B': '2루수',
  '3B': '3루수',
  'SS': '유격수',
  'DH': '지명타자',
  'CF': '중견수',
  'LF': '좌익수',
  'RF': '우익수',
}

const getDateParam = (date: Date) => {
    const yearStyr = date.getFullYear().toString().slice(2);
    const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
    const dateStr = date.getDate().toString().padStart(2, '0');
    return `${yearStyr}${monthStr}${dateStr}`;
};

interface PlayerSlot extends KBOLeaguePositionRuleDto {
    playerId?: number;
    positionIndex: number;
    key: string;
}

const DateTabs: React.FC<{
    league: LeagueDto;
    activeDate: string;
    activeView: 'lineup' | 'standings';
    onDateChange: (date: string) => void;
    onViewChange: (view: 'lineup' | 'standings') => void;
}> = ({ league, activeDate, activeView, onDateChange, onViewChange }) => {

    const dates: { dateStr: string; dayOfWeek: string; }[] = [];
    const startDate = new Date(league.start_date);
    const endDate = new Date(league.end_date);
    const dateSpan = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    for (let i = 0; i <= dateSpan; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);

        const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
        const gameDateStr = getDateParam(date);
        dates.push({ dateStr: gameDateStr, dayOfWeek });
        if (gameDateStr === league.end_date) break;
    }

    return (
        <div className="mt-2 mb-2">
            <ul className="nav nav-fill nav-pills">
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeView === 'standings' ? 'active' : ''}`}
                        onClick={() => onViewChange('standings')}
                    >
                        현재 순위
                    </button>
                </li>

                {dates.map(({ dateStr, dayOfWeek }) => (
                    <li className="nav-item" key={dateStr}>
                        {/* NavLink 대신 button을 사용하여 상태를 직접 제어합니다. */}
                        <button
                            className={`nav-link ${activeView !== 'standings' && dateStr === activeDate ? 'active' : ''}`}
                            onClick={() => onDateChange(dateStr)}
                        >
                            {`${dateStr.slice(2, 4)}/${dateStr.slice(4, 6)} (${dayOfWeek})`}
                        </button>
                    </li>
                ))}
            </ul>
            <style>
            {`
                 /* Bootstrap의 기본 스타일을 덮어쓰기 위해 더 구체적인 선택자 사용 */
                .nav-pills .nav-link {
                    background-color: #fff;
                    color: #0d6efd;
                    border: 1px solid #dee2e6;
                    margin: 0 2px;
                    transition: all 0.2s ease-in-out;
                    font-weight: 500;
                }

                .nav-pills .nav-link:not(.active):hover {
                    background-color: #e9ecef;
                }

                /* 활성화된 탭의 스타일 */
                .nav-pills .nav-link.active {
                    background-color: #0d6efd;
                    color: white;
                    font-weight: bold;
                    border-color: #0d6efd;
                }
            `}
            </style>
           
        </div>
        
    );
};

const StandingsTable: React.FC<LineupComponentProps> = ({ league }) => {
    const navigate = useNavigate();
    const { user, fetchApi } = useAuth();
    const [data, setData] = useState<LeagueSessionParticipantRankResponse | null>(null);

    const fetchRankData = useCallback(async () => {
        const res: DataResponse<LeagueSessionParticipantRankResponse> = await fetchApi(`/api/league/session/${league.code}/rank`);
        setData(res.data);
    }, [league, fetchApi])

    useEffect(() => {
        fetchRankData();
    }, [fetchRankData]);
    
    return (
        <>
            <div className="card shadow-sm">
                <div className="card-body">
                    <h3 className="card-title mb-4">현재 리그 순위</h3>
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>순위</th>
                                <th>참가자</th>
                                <th>총 점수</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.rankList.map(r => (
                                <tr key={r.user_code}>
                                    <td className="fw-bold">{r.rank}</td>
                                    <td className={r.user_code === league.my_user_code ? 'fw-bold' : ''}>{r.name}</td>
                                    <td>{r.score.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="container mt-5">
                <div className="row">
                    <div className="col-lg-6">
                        <div className="card shadow-lg mb-4 clickable-card" onClick={() => navigate('/user-ranking')} style={{ cursor: 'pointer' }}>
                            <div className="card-header bg-success text-white">
                                <h3 className="h5 mb-0">일일 명예의 전당</h3>
                            </div>
                            <div className="card-body">
                                <NewPodium topRanks={data?.dailyRankList ?? []} user={user} />
                            </div>
                            <div className="card-footer text-end">
                                <span className="btn btn-sm btn-outline-secondary">자세히 보기 <i className="bi bi-arrow-right"></i></span>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card shadow-lg mb-4 clickable-card" onClick={() => navigate('/user-ranking')} style={{ cursor: 'pointer' }}>
                            <div className="card-header bg-success text-white">
                                <h3 className="h5 mb-0">주간 명예의 전당</h3>
                            </div>
                            <div className="card-body">
                                <NewPodium topRanks={data?.weeklyRankList ?? []} user={user} />
                            </div>
                            <div className="card-footer text-end">
                                <span className="btn btn-sm btn-outline-secondary">자세히 보기 <i className="bi bi-arrow-right"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}


// Player Stats 모달 컴포넌트 추가
const PlayerStatsModal: React.FC<{ 
    player: LeagueLineupPlayerDto; 
    gameDate: string;
    onClose: () => void 
}> = ({ player, gameDate, onClose }) => {
    
    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const isPitcher = player.position === 'P';
    const stats = player.stats;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h5 className="modal-title">{player.name} ({POSITION_MAP[player.position] ?? player.position} - {TEAM_NAME_MAP[player.team] ?? player.team})</h5>
                    <button type="button" className="close-button" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    <h6>{gameDate} 경기 스탯</h6>
                    {stats ? (
                        <>
                            <p>총 점수: {player.score.toFixed(2)}</p>
                            {isPitcher ? (
                                <table className="table table-bordered table-sm">
                                    <thead>
                                        <tr>
                                            <th>이닝</th>
                                            <th>피안타</th>
                                            <th>피홈런</th>
                                            <th>볼넷</th>
                                            <th>삼진</th>
                                            <th>승</th>
                                            <th>패</th>
                                            {/* <th>세이브</th> */}
                                            {/* <th>홀드</th> */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{`${Math.ceil((stats as KBOGamePlayerStatsPitcherDataDto).sIP3 / 3)}.${(stats as KBOGamePlayerStatsPitcherDataDto).sIP3 % 3}`}</td>
                                            <td>{(stats as KBOGamePlayerStatsPitcherDataDto).sH}</td>
                                            <td>{(stats as KBOGamePlayerStatsPitcherDataDto).sHR}</td>
                                            <td>{(stats as KBOGamePlayerStatsPitcherDataDto).sBB}</td>
                                            <td>{(stats as KBOGamePlayerStatsPitcherDataDto).sSO}</td>
                                            <td>{(stats as KBOGamePlayerStatsPitcherDataDto).sWIN}</td>
                                            <td>{(stats as KBOGamePlayerStatsPitcherDataDto).sLOSE}</td>
                                            {/* <td>{(stats as KBOGamePlayerStatsPitcherDataDto).sSAVE}</td> */}
                                            {/* <td>{(stats as KBOGamePlayerStatsPitcherDataDto).sHOLD}</td> */}
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <table className="table table-bordered table-sm">
                                    <thead>
                                        <tr>
                                            <th>득점</th>
                                            <th>안타</th>
                                            <th>2루타</th>
                                            <th>3루타</th>
                                            <th>홈런</th>
                                            <th>타점</th>
                                            <th>볼넷</th>
                                            <th>삼진</th>
                                            <th>도루</th>
                                            <th>병살</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{(stats as KBOGamePlayerStatsHitterDataDto).sR}</td>
                                            <td>{(stats as KBOGamePlayerStatsHitterDataDto).sH}</td>
                                            <td>{(stats as KBOGamePlayerStatsHitterDataDto).s2B}</td>
                                            <td>{(stats as KBOGamePlayerStatsHitterDataDto).s3B}</td>
                                            <td>{(stats as KBOGamePlayerStatsHitterDataDto).sHR}</td>
                                            <td>{(stats as KBOGamePlayerStatsHitterDataDto).sRBI}</td>
                                            <td>{(stats as KBOGamePlayerStatsHitterDataDto).sBB}</td>
                                            <td>{(stats as KBOGamePlayerStatsHitterDataDto).sSO}</td>
                                            <td>{(stats as KBOGamePlayerStatsHitterDataDto).sSB}</td>
                                            <td>{(stats as KBOGamePlayerStatsHitterDataDto).sGDP}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </>
                    ) : (
                        <p>오늘 경기에 대한 기록이 없습니다.</p>
                    )}
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>닫기</button>
                </div>
            </div>
            <style>
                {`/* Modal Styles */
                .modal-backdrop { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background-color: rgba(0, 0, 0, 0.6); display: flex; justify-content: center; align-items: center; z-index: 1050; }
                .modal-content { background-color: white; padding: 20px; border-radius: 12px; box-shadow: 0 5px 15px rgba(0,0,0,.5); width: 90%; max-width: 600px; }
                .modal-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #dee2e6; padding-bottom: 15px; margin-bottom: 20px; }
                .modal-title { margin: 0; font-size: 1.5rem; font-weight: bold; color: #333; }
                .close-button { border: none; background: none; font-size: 1.75rem; font-weight: bold; cursor: pointer; padding: 0; line-height: 1; }
                .modal-body { margin-bottom: 20px; text-align: center; }
                .modal-body h6 { font-size: 1.1rem; color: #6c757d; margin-bottom: 1rem; }
                .modal-body p { font-size: 1.2rem; margin: 0.5rem 0; font-weight: 500; color: #212529; }
                .modal-footer { border-top: 1px solid #dee2e6; padding-top: 15px; text-align: right; }
                .btn-secondary { padding: 8px 16px; border-radius: 4px; border: 1px solid #ccc; cursor: pointer; background-color: #6c757d; color: white; }
                .modal-body table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                .modal-body th, .modal-body td { border: 1px solid #ddd; padding: 8px; text-align: center; }
                .modal-body th { background-color: #f2f2f2; }
                `}
            </style>
        </div>
    );
};

const PlayerTable: React.FC<{
    title: string;
    players: LeagueLineupPlayerDto[]; // 전체 드래프트된 선수 목록
    positionSlots: PlayerSlot[]; // 표시할 포지션 슬롯 목록 (선수 ID 포함)
    onMove: (playerId: number, positionIndex: number) => void;
    selectedInfo: { id: number | null, fromIndex: number } | null;
    isFixed: boolean;
    onScoreClick: (player: LeagueLineupPlayerDto) => void; // onScoreClick prop 추가
}> = ({ title, players, positionSlots, onMove, selectedInfo, isFixed, onScoreClick}) => {

    const playerMap = useMemo(() => new Map(players.map(p => [p.id, p])), [players]);

    const isSelectable = (positionList: string[]) => {
        if (!selectedInfo || selectedInfo.id === null) return false;
        const selectedPlayer = playerMap.get(selectedInfo.id);
        return selectedPlayer ? positionList.length === 0 || positionList.includes(selectedPlayer.position) : true;
    };

    const benchList = useMemo(() => {
        const slotPlayerIdSet = new Set(positionSlots.filter(slot => slot.playerId).map(slot => slot.playerId));
        return players.filter(player => !slotPlayerIdSet.has(player.id)).map(player => player.id);
    }, [positionSlots, players]);

    return (
        <>
            <thead>
                <tr className='border-0'>
                    <th className="col-fit-content text-start fs-4 border-0 pt-3" colSpan={!isFixed ? 7 : 6}>{title}</th>
                </tr>
                <tr>
                    <th className="col-fit-content">슬롯</th>
                    <th className="col-fit-content">포지션</th>
                    <th className="col-fit-content">선수</th>
                    <th className="col-player-name">선수명</th>
                    <th className="col-fit-content">팀</th>
                    {!isFixed && <th className="col-fit-content">이동</th>}
                    <th className="col-fit-content">점수</th>
                </tr>
            </thead>
            <tbody>
            {
                positionSlots.map((pos) => {
                    const player = pos.playerId ? playerMap.get(pos.playerId)! : null;
                    const isCurrentlySelected = selectedInfo?.id === pos.playerId && selectedInfo?.fromIndex === pos.positionIndex;
                    const canMoveTo = isSelectable(pos.position_list);
                    const default_image = '/default-image.png'
                    return (
                        <tr key={pos.key}>
                            <td className="col-fit-content">{pos.name}</td>
                            <td className="col-fit-content">{player ? (POSITION_MAP[player.position] ?? player.position) : ('-')}</td>
                            <td className="col-fit-content">
                                {
                                    player && 
                                    <img
                                    src={player.profile_image || default_image}
                                    alt={`${player.name} 선수 사진`}
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        marginBottom: '15px'
                                    }}/> 
                                }
                            </td>
                            <td className="col-player-name">
                                {player ? (
                                    <Link to={`/player/${player.id}`}>{player.name}</Link>
                                ) : (
                                    '—'
                                )}
                            </td>
                            <td className="col-fit-content">{player ? TEAM_NAME_MAP[player.team] ?? player.team : '—'}</td>
                            {!isFixed && (
                                <td className="col-fit-content">
                                    <button
                                        className={`btn btn-sm ${isCurrentlySelected ? 'btn-danger' : (canMoveTo ? 'btn-success' : 'btn-outline-info')}`}
                                        onClick={() => onMove(pos.playerId ?? 0, pos.positionIndex)}
                                    >
                                        {isCurrentlySelected ? '선택 취소' : (canMoveTo ? '여기로' : '이동')}
                                    </button>
                                </td>
                            )}
                            <td className="col-fit-content">
                                    {player?.score !== null && player?.score !== undefined ? ( // 점수가 null 또는 undefined가 아닐 때만 클릭 가능
                                    <span 
                                        onClick={() => player && onScoreClick(player)} 
                                        style={{ cursor: 'pointer', color: '#0d6efd', fontWeight: 'bold' }}
                                        title="클릭하여 상세 스탯 보기"
                                    >
                                        {player.score.toFixed(2)}
                                    </span>
                                ) : (
                                    '—'
                                )}
                            </td>
                        </tr>
                    );
                })
            }
            {
                benchList.map(playerId => {
                    const player = playerMap.get(playerId);
                    const isCurrentlySelected = selectedInfo?.id === playerId && selectedInfo?.fromIndex === -1;
                    const canMoveTo = isSelectable([]);
                    const default_image = '/default-image.png'
                    return (
                        <tr key={`bench-${playerId}`}>
                            <td className="col-fit-content">Bench</td>
                            <td className="col-fit-content">{player ? (POSITION_MAP[player.position] ?? player.position) : ('-')}</td>
                            <td className="col-fit-content">
                                {
                                    player && 
                                    <img
                                    src={player.profile_image || default_image}
                                    alt={`${player.name} 선수 사진`}
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        marginBottom: '15px'
                                    }}/> 
                                }
                            </td>
                            <td className="col-player-name">
                                {player ? (
                                    <Link to={`/player/${player.id}`}>{player.name}</Link>
                                ) : (
                                    '—'
                                )}
                            </td>
                            <td className="col-fit-content">{player ? TEAM_NAME_MAP[player.team] ?? player.team : '—'}</td>
                            {!isFixed && (
                                <td className="col-fit-content">
                                    <button
                                        className={`btn btn-sm ${isCurrentlySelected ? 'btn-danger' : (canMoveTo ? 'btn-success' : 'btn-outline-info')}`}
                                        onClick={() => onMove(playerId ?? 0, -1)}
                                    >
                                        {isCurrentlySelected ? '선택 취소' : (canMoveTo ? '여기로' : '이동')}
                                    </button>
                                </td>
                            )}
                            <td className="col-fit-content">
                                    {player?.score !== null && player?.score !== undefined ? ( // 점수가 null 또는 undefined가 아닐 때만 클릭 가능
                                    <span 
                                        onClick={() => player && onScoreClick(player)} 
                                        style={{ cursor: 'pointer', color: '#0d6efd', fontWeight: 'bold' }}
                                        title="클릭하여 상세 스탯 보기"
                                    >
                                        {player.score.toFixed(2)}
                                    </span>
                                ) : (
                                    '—'
                                )}
                            </td>
                        </tr>
                    );
                })
            }
            {
                selectedInfo && <tr key={`bench-empty`}>
                    <td className="col-fit-content">Bench</td>
                    <td className="col-fit-content">-</td>
                    <td className="col-fit-content"></td>
                    <td className="col-player-name">-</td>
                    <td className="col-fit-content">-</td>
                    {!isFixed && (
                        <td className="col-fit-content">
                            <button
                                className={`btn btn-sm btn-success`}
                                onClick={() => onMove(0, -1)}
                            >
                                여기로
                            </button>
                        </td>
                    )}
                    <td className="col-fit-content">-</td>
                </tr>
            }
            </tbody>
        </>
    );
};


// --- 메인 라인업 페이지 컴포넌트 ---
const LineupComponent: React.FC<LineupComponentProps> = ({league}) => {
    const location = useLocation();
    const navigate = useNavigate()
    const { fetchApi } = useAuth();
    const [positionList, setPositionList] = useState<KBOLeaguePositionRuleDto[]>([]);
    const [lineupData, setLineupData] = useState<LeagueDailyLineupPlayerResponse | null>(null);
    const [view, setView] = useState<'lineup' | 'standings'>('lineup');
    const [loadingCount, setLoadingCount] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [selectedPlayer, setSelectedPlayer] = useState<{ id: number | null; fromIndex: number } | null>(null);

    // 새 상태: 선수 스탯 모달 관련
    const [showPlayerStatsModal, setShowPlayerStatsModal] = useState<boolean>(false);
    const [playerStatsModalData, setPlayerStatsModalData] = useState<LeagueLineupPlayerDto | null>(null);

    //const isViewingOtherUser = useMemo(() => selectedUserId !== currentUserId, [selectedUserId, currentUserId]);
    const isViewingOtherUser = false;

    const gameDate = useMemo(() => {
        const queryParams = new URLSearchParams(location.search);
        return queryParams.get('date') || getDateParam(new Date(league.start_date));
    }, [location.search, league.start_date]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        if (!queryParams.has('date')) {
            const today = new Date();    
            const startDate = new Date(league.start_date);
            const endDate = new Date(league.end_date);
    
            let navigateDate: Date;
            if (today < startDate) {
                navigateDate = startDate;
            } else if (today > endDate) {
                navigateDate = endDate;
            } else {
                navigateDate = today;
            }

            navigate(`?date=${getDateParam(navigateDate)}`, { replace: true });
        }
    }, [location.search, navigate, league.start_date, league.end_date]);

    const fetchRuleData = useCallback(async () => {
        if (!league.code) return;

        setLoadingCount(cnt => cnt + 1);
        try {
            const res: DataResponse<KBOLeagueRuleResponse> = await fetchApi(`/api/league/session/${league.code}/rule`);
            setPositionList(res.data?.position_list ?? []);
        } catch (err: any) {
            setError(err.message || '라인업 정보를 불러오는 데 실패했습니다.');
        } finally {
            setLoadingCount(cnt => cnt - 1);
        }
    }, [league.code, fetchApi]);

    const fetchLineupData = useCallback(async () => {
        if (!league.code) return;

        setLoadingCount(cnt => cnt + 1);
        try {
            const res: LeagueDailyLineupPlayerResponse = await fetchApi(`/api/league/session/${league.code}/lineup?date=${gameDate}`);
            setLineupData(res);
        } catch (err: any) {
            setError(err.message || '라인업 정보를 불러오는 데 실패했습니다.');
        } finally {
            setLoadingCount(cnt => cnt - 1);
        }
    }, [league.code, gameDate, fetchApi]);

    const handleMove = useCallback(async (targetPlayerId: number, targetIndex: number) => {
        if (!lineupData) return;

        if (lineupData.isFixed) return;

        if (!selectedPlayer) {
            setSelectedPlayer({ id: targetPlayerId, fromIndex: targetIndex });
        } else {
            const { id: sourcePlayerId, fromIndex: sourceIndex } = selectedPlayer;
            
            if (sourceIndex === targetIndex) {
                // 선택 해제
                setSelectedPlayer(null);
                return;
            }

            const updatePlayerIdList = [...(lineupData.playerIdList || positionList.filter(pos => pos.is_active).map(_ => 0))];
            updatePlayerIdList[sourceIndex] = targetPlayerId;
            if (targetIndex >= 0) {
                updatePlayerIdList[targetIndex] = sourcePlayerId ?? 0;
            }
            
            try {
                await fetchApi(`/api/league/session/${league.code}/lineup?date=${gameDate}`, {
                    method: 'PUT',
                    body: JSON.stringify({
                        game_date: gameDate,
                        player_id_list: updatePlayerIdList.join(','),
                    }),
                });
            } catch (err: any) {
                alert(err.message || '라인업 저장에 실패했습니다.');
            } finally {
                setSelectedPlayer(null);
                fetchLineupData();
            }
        }
    }, [lineupData, selectedPlayer, positionList, fetchApi, league.code, gameDate, fetchLineupData]);

    const handleViewChange = (newView: 'lineup' | 'standings') => {
        setView(newView);
    };

    const handleDateChange = (newDate: string) => {
        handleViewChange('lineup');
        setSelectedPlayer(null);
        navigate(`?date=${newDate}`, { replace: true });
    };

    // 점수 클릭 핸들러
    const handleScoreClick = useCallback((player: LeagueLineupPlayerDto) => {
        setPlayerStatsModalData(player);
        setShowPlayerStatsModal(true);
    }, []);

    useEffect(() => {
        fetchRuleData();
        fetchLineupData();
    }, [fetchLineupData, fetchRuleData]);

    const renderContent = () =>{
        if (loadingCount) return <div>라인업 정보를 불러오는 중...</div>;
        if (error) return <div className="alert alert-danger">{error}</div>;
        

        if (view === 'standings') {
            return <StandingsTable league={league} />;
        }

        if (view === 'lineup') {
            if (!lineupData) return <div>라인업 정보가 없습니다.</div>;

            const { isFixed, lineupPlayerList, playerIdList} = lineupData;

            const playerMap = new Map(lineupPlayerList.map(p => [p.id, p]));

            const playerSlotList = positionList
                .filter(pos => pos.is_active)
                .map((pos, i) => ({
                    ...pos,
                    playerId: playerIdList[i] ?? 0,
                    positionIndex: i,
                    key: `player-slot-${i}`,
                }));

            return (
                <div className="container ">
                    {/* 여기에 스타일을 추가합니다. */}
                    <style>
                        {`
                            .col-fit-content {
                                /* 너비를 1%로 설정하고 줄바꿈을 방지하여 내용에 딱 맞게 만듭니다. */
                                width: 1%;
                                white-space: nowrap;
                                text-align: center; /* 내용은 중앙 정렬 */
                            }
                            .col-player-name {
                                /* 이 열이 남은 공간을 모두 차지하도록 합니다. */
                                width: 100%;
                                white-space: nowrap;
                                text-align: center;
                            }

                            @media (min-width: 992px) {
                                .table th.col-fit-content,
                                .table td.col-fit-content,
                                .table th.col-player-name,
                                .table td.col-player-name {
                                    width: auto;
                                    white-space: normal; /* 다시 줄바꿈 허용 */
                                }
                            }
                        `}
                    </style>
                    
                    {isFixed && <div className="alert alert-warning">경기가 시작되어 라인업을 수정할 수 없습니다.</div>}
                    {selectedPlayer && !isFixed && <div className="alert alert-info">교체할 위치를 선택하세요. (현재 선택: {playerMap.get(selectedPlayer.id!)?.name || '빈 슬롯'})</div>}
                    <div className='mb-5'>
                        <div className="table-responsive">
                            <table className="table table-striped table-bordered">
                                <PlayerTable
                                    title=""
                                    players={lineupPlayerList}
                                    positionSlots={playerSlotList}
                                    onMove={handleMove}
                                    selectedInfo={selectedPlayer}
                                    isFixed={isFixed || isViewingOtherUser}
                                    onScoreClick={handleScoreClick} // prop 전달
                                />
                            </table>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }

    return (
        <div className="container">
           <section className="guide-hero-section py-5 fade-in">
                        <h1 className="text-center display-5 fw-bolder">
                        {league.name} 라인업 관리 
                        </h1>
            </section>
            <DateTabs league={league} activeDate={gameDate} activeView={view} onDateChange={handleDateChange} onViewChange={handleViewChange} />
            {renderContent()}

            {/* PlayerStatsModal 조건부 렌더링 */}
            {showPlayerStatsModal && playerStatsModalData && (
                <PlayerStatsModal
                    player={playerStatsModalData}
                    onClose={() => setShowPlayerStatsModal(false)}
                    gameDate={gameDate}
                />
            )}
            <AdComponent />
        </div>
    );
};

export default LineupComponent;